import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import OTABookingsList from 'features/MonthlyOTAConversions/OTABookingsList'
import { useInternalAdmin } from 'hooks'
import moment from 'moment'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './MonthlyOTAConversions.module.scss'
import { OTAConversionsFilters } from './OTAConversionsFilter'
import RevenueTooltip from './RevenueTooltip'
import { colors, currencyFormatter } from './utils/constants'
import { CustomLabel } from './utils/CustomLabel'
import useOTAConversionsData from './utils/useOTAConversionsData'

const MonthlyOTAConversions = () => {
  const [activeYear, setActiveYear] = useState(moment().format('YYYY'))
  const [activeMonth, setActiveMonth] = useState()
  const [stateToFilter, setStateToFilter] = useState('')
  const [cityToFilter, setCityToFilter] = useState('')
  const [groupToFilter, setGroupToFilter] = useState()
  const [showAllHotels, setShowAllHotels] = useState(false)
  const [showWholeGroup, setShowWholeGroup] = useState(false)
  const [parentChain, setParentChain] = useState()
  const [isOtaOnly, setIsOTAOnly] = useState(true)

  const isInternalAdmin = useInternalAdmin()
  const isDemoMode = useSelector(s => s.login.isDemoMode)
  const ref = useRef()

  let kind = 'CurrentSubsidiary'
  if (showWholeGroup) kind = 'WholeGroup'
  if (showAllHotels) kind = 'AllSubsidiaries'

  const { isLoading, isErred, response, availableCities, hotelGroups } =
    useOTAConversionsData({
      isOtaOnly,
      activeYear,
      kind,
      parentChain,
      cityToFilter,
      groupToFilter,
      stateToFilter
    })

  function handleBarClick(data) {
    if (data?.activePayload?.length) {
      setActiveMonth(data.activePayload[0].payload.month)
    } else {
      setActiveMonth(null)
    }
  }

  function handleChangeState(state) {
    setStateToFilter(state)
    setCityToFilter('')
  }

  function handleChangeGroupFilter(value) {
    if (value === 'currentHotel') {
      setShowWholeGroup(false)
      setShowAllHotels(false)
      setGroupToFilter()
    } else if (value === 'allHotels') {
      setShowWholeGroup(false)
      setShowAllHotels(true)
      setGroupToFilter()
    } else {
      setGroupToFilter(value)
      setShowAllHotels(true)
      setShowWholeGroup(false)
    }
  }

  if (isLoading) return <LoadingIndicator />
  if (isErred) return <p>Something went wrong</p>

  const years = Array(
    Number(moment().format('YYYY')) -
      Number(moment(response?.serviceStartDate || 2018).format('YYYY')) +
      1
  )
    .fill(0)
    .map((_, i) => Number(moment().format('YYYY')) - i)

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Monthly OTA conversions" width={1080} />
      <div className={styles.topBar}>
        {!showAllHotels && (
          <p className={styles.serviceStartDate}>
            <InfoIcon />
            Directful service launched on{' '}
            {moment(response.serviceStartDate).format('MMMM Do, YYYY')}
          </p>
        )}
        <OTAConversionsFilters
          isOtaOnly={isOtaOnly}
          setIsOTAOnly={setIsOTAOnly}
          isInternalAdmin={isInternalAdmin}
          showAllHotels={showAllHotels}
          showWholeGroup={showWholeGroup}
          stateToFilter={stateToFilter}
          handleChangeState={handleChangeState}
          availableCities={availableCities}
          cityToFilter={cityToFilter}
          setCityToFilter={setCityToFilter}
          parentChain={parentChain}
          setParentChain={setParentChain}
          hotelGroups={hotelGroups}
          handleChangeGroupFilter={handleChangeGroupFilter}
          activeYear={activeYear}
          setActiveYear={setActiveYear}
          years={years}
          isDemoMode={isDemoMode}
        />
      </div>
      <div className={styles.chartsHolder} ref={ref}>
        <div className={styles.chartBox}>
          <div className={styles.summaryHolder}>
            <div className={styles.summaryValueHolder}>
              <p className={styles.summaryLabel}>Conversion revenue</p>
              <p className={styles.summaryValue}>
                {currencyFormatter.format(response.totalRevenue)}
              </p>
            </div>
          </div>
          <div style={{ height: 300, width: 'auto', paddingTop: 30 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                onClick={handleBarClick}
                syncId="syncId"
                data={response.monthlyRevenue}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 15
                }}
              >
                <CartesianGrid stroke="#F2F2F2" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                  padding={{ left: 20, right: 20 }}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(0, 14)'
                  }}
                  tickSize={0}
                />
                <YAxis
                  tickSize={0}
                  axisLine={false}
                  allowDecimals={false}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(-2, 0)'
                  }}
                  range
                  tickFormatter={value => currencyFormatter.format(value)}
                />
                <Tooltip
                  content={
                    <RevenueTooltip
                      labelFormatter={label => `${label} ${response.activeYear}`}
                      valueFormatter={value => currencyFormatter.format(value)}
                      dataTitle="Revenue"
                    />
                  }
                  cursor={{
                    stroke: 'red',
                    strokeWidth: 0,
                    fill: '#dedede',
                    opacity: 0.22
                  }}
                />
                {response.activeYear ===
                  moment(response.serviceStartDate).format('YYYY') && (
                  <ReferenceLine
                    x={moment(response.serviceStartDate).format('MMM')}
                    strokeDasharray="3 3"
                    label={CustomLabel}
                    stroke="#2158CF"
                  />
                )}
                <Bar
                  dataKey="value"
                  // fill={colors.revenue}
                  barSize={7}
                >
                  {response.monthlyRevenue.map((entry, index) => (
                    <Cell
                      cursor="pointer"
                      fill={
                        activeMonth
                          ? entry.month === activeMonth
                            ? '#82ca9d'
                            : '#D1EADD'
                          : '#82ca9d'
                      }
                      key={`cell-${index}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={styles.chartBox}>
          <div className={styles.summaryHolder}>
            <div className={styles.summaryValueHolder}>
              <p className={styles.summaryLabel}>Number of bookings</p>
              <p className={styles.summaryValue}>
                {formatNumberAsThousands(response.totalNumberOfBookings)}
              </p>
            </div>
          </div>
          <div style={{ height: 300, width: 'auto', paddingTop: 30 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                onClick={handleBarClick}
                syncId="syncId"
                data={response.monthlyConversions}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 15
                }}
              >
                <CartesianGrid stroke="#F2F2F2" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(0, 14)'
                  }}
                  tickSize={0}
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis
                  tickSize={0}
                  axisLine={false}
                  allowDecimals={false}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(-10, 0)'
                  }}
                  range
                />
                <Tooltip
                  content={
                    <RevenueTooltip
                      labelFormatter={label => `${label} ${response.activeYear}`}
                      dataTitle="Bookings"
                    />
                  }
                  cursor={{
                    stroke: 'red',
                    strokeWidth: 0,
                    fill: '#dedede',
                    opacity: 0.22
                  }}
                />
                {response.activeYear ===
                  moment(response.serviceStartDate).format('YYYY') && (
                  <ReferenceLine
                    x={moment(response.serviceStartDate).format('MMM')}
                    strokeDasharray="3 3"
                    label={CustomLabel}
                    stroke="#2158CF"
                  />
                )}
                <Bar
                  dataKey="value"
                  fill={colors.bookings}
                  barSize={7}
                  onClick={handleBarClick}
                >
                  {response.monthlyConversions.map((entry, index) => (
                    <Cell
                      cursor="pointer"
                      fill={
                        activeMonth
                          ? entry.month === activeMonth
                            ? '#667EEA'
                            : '#EBECF1'
                          : '#667EEA'
                      }
                      key={`cell-${index}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <br />
      <OTABookingsList
        isOtaOnly={isOtaOnly}
        month={activeMonth}
        year={response.activeYear}
        kind={kind}
        parentChain={parentChain}
        cityToFilter={cityToFilter}
        groupToFilter={groupToFilter}
        stateToFilter={stateToFilter}
      />
    </div>
  )
}

export default MonthlyOTAConversions
